import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Grid,
  Avatar,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  People,
  CheckCircle,
  Cancel,
  PersonRemove,
  EmojiEvents,
  SportsTennis,
  Share,
  PlayArrow,
  Info,
  PersonAdd,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { Tournament, TournamentParticipant, Match, User } from '../../types';
import apiService from '../../services/api';

function participantDisplayLabel(participant: TournamentParticipant): string {
  const team = participant.team_name?.trim();
  const players = [participant.player1_name, participant.player2_name].filter(Boolean).join(' & ');
  if (team && players) return `${team} (${players})`;
  return team || players || `Participant #${participant.id}`;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tournament-tabpanel-${index}`}
      aria-labelledby={`tournament-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminTournamentManagementPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedMatchForReject, setSelectedMatchForReject] = useState<Match | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);
  const [canLaunchMontanteRound, setCanLaunchMontanteRound] = useState<{
    can_launch: boolean;
    reason?: string;
    next_round: number;
  } | null>(null);
  const [launchingMontanteRound, setLaunchingMontanteRound] = useState(false);
  const [canLaunchMexicanoRound, setCanLaunchMexicanoRound] = useState<{
    can_launch: boolean;
    reason?: string;
    next_round: number;
  } | null>(null);
  const [launchingMexicanoRound, setLaunchingMexicanoRound] = useState(false);
  const [canLaunchAmericanoRound, setCanLaunchAmericanoRound] = useState<{
    can_launch: boolean;
    reason?: string;
    next_round: number;
  } | null>(null);
  const [launchingAmericanoRound, setLaunchingAmericanoRound] = useState(false);
  const [clubs, setClubs] = useState<any[]>([]);
  const [courts, setCourts] = useState<any[]>([]);
  const [organizers, setOrganizers] = useState<{ user_id: number; user_name?: string; added_at?: string }[]>([]);
  const [addOrganizerDialogOpen, setAddOrganizerDialogOpen] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<User | null>(null);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    max_participants: 16,
    min_skill_level: 1,
    max_skill_level: 10,
    format: 'round_robin' as Tournament['format'],
    status: 'upcoming' as 'upcoming' | 'registration' | 'active' | 'completed' | 'cancelled',
    format_config: null as { num_groups?: number; num_rounds?: number; max_teams_per_court?: number; court_ids?: number[] } | null,
    club_ids: [] as number[],
  });
  // const [errors, setErrors] = useState<Record<string, string>>({});
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user?.is_admin) {
      loadTournaments();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTournament) {
      loadParticipants(selectedTournament.id);
      loadMatches(selectedTournament.id);
      loadOrganizers(selectedTournament.id);
      loadUsersForOrganizer(); // Preload for organizer name fallback
    } else {
      setOrganizers([]);
    }
  }, [selectedTournament]);

  useEffect(() => {
    if (addOrganizerDialogOpen) {
      loadUsersForOrganizer();
    }
  }, [addOrganizerDialogOpen]);

  // Load courts for Montante when participating clubs change in create/edit dialog
  useEffect(() => {
    if (!openDialog || formData.format !== 'montante_descendante') {
      return;
    }
    const ids = formData.club_ids || [];
    if (ids.length === 0) {
      setCourts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const courtsRes = await apiService.getCourts(ids);
        if (!cancelled) setCourts(courtsRes.data || []);
      } catch {
        if (!cancelled) setCourts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openDialog, formData.format, formData.club_ids]);

  // Fetch can-launch-round for Montante tournaments
  useEffect(() => {
    const fetchCanLaunch = async () => {
      if (
        !selectedTournament ||
        (String(selectedTournament.format || '').toLowerCase() !== 'montante_descendante' &&
          String(selectedTournament.format || '').toLowerCase() !== 'montantedescendante') ||
        selectedTournament.status !== 'active'
      ) {
        setCanLaunchMontanteRound(null);
        return;
      }
      try {
        const response = await apiService.canLaunchMontanteRound(selectedTournament.id);
        setCanLaunchMontanteRound(response.data);
      } catch {
        setCanLaunchMontanteRound(null);
      }
    };
    fetchCanLaunch();
  }, [selectedTournament?.id, selectedTournament?.format, selectedTournament?.status, matches]);

  useEffect(() => {
    const fetchCanLaunch = async () => {
      if (
        !selectedTournament ||
        String(selectedTournament.format || '').toLowerCase() !== 'mexicano' ||
        selectedTournament.status !== 'active'
      ) {
        setCanLaunchMexicanoRound(null);
        return;
      }
      try {
        const response = await apiService.canLaunchMexicanoRound(selectedTournament.id);
        setCanLaunchMexicanoRound(response.data);
      } catch {
        setCanLaunchMexicanoRound(null);
      }
    };
    fetchCanLaunch();
  }, [selectedTournament?.id, selectedTournament?.format, selectedTournament?.status, matches]);

  useEffect(() => {
    const fetchCanLaunch = async () => {
      if (
        !selectedTournament ||
        String(selectedTournament.format || '').toLowerCase() !== 'americano' ||
        selectedTournament.status !== 'active'
      ) {
        setCanLaunchAmericanoRound(null);
        return;
      }
      try {
        const response = await apiService.canLaunchAmericanoRound(selectedTournament.id);
        setCanLaunchAmericanoRound(response.data);
      } catch {
        setCanLaunchAmericanoRound(null);
      }
    };
    fetchCanLaunch();
  }, [selectedTournament?.id, selectedTournament?.format, selectedTournament?.status, matches]);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getAllTournaments();
      setTournaments(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async (tournamentId: number) => {
    try {
      const response = await apiService.getTournamentParticipants(tournamentId);
      setParticipants(response.data || []);
    } catch (err: any) {
      console.error('Failed to load participants:', err);
    }
  };

  const loadMatches = async (tournamentId: number) => {
    try {
      setLoadingMatches(true);
      const response = await apiService.getTournamentMatches(tournamentId);
      setMatches(response.data || []);
    } catch (err: any) {
      console.error('Failed to load matches:', err);
      setError(err.response?.data?.detail || 'Failed to load matches');
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadOrganizers = async (tournamentId: number) => {
    try {
      setLoadingOrganizers(true);
      const response = await apiService.getTournamentOrganizers(tournamentId);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data as any)?.organizers ?? (data as any)?.data ?? [];
      setOrganizers(list);
    } catch (err: any) {
      console.error('Failed to load organizers:', err);
      setOrganizers([]);
    } finally {
      setLoadingOrganizers(false);
    }
  };

  const loadUsersForOrganizer = async () => {
    try {
      const response = await apiService.getAllUsers();
      const users = Array.isArray(response.data) ? response.data : (response.data as any)?.users || [];
      setUsersList(users);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setUsersList([]);
    }
  };

  const handleAddOrganizer = async () => {
    if (!selectedTournament || !selectedUserToAdd) return;
    try {
      await apiService.addTournamentOrganizer(selectedTournament.id, selectedUserToAdd.id);
      setSuccess('Organizer added successfully');
      loadOrganizers(selectedTournament.id);
      setAddOrganizerDialogOpen(false);
      setSelectedUserToAdd(null);
      loadTournaments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add organizer');
    }
  };

  const handleRemoveOrganizer = async (userId: number) => {
    if (!selectedTournament) return;
    const creatorId = (selectedTournament as any).created_by;
    if (userId === creatorId) {
      setError(t('adminTournament.cannotRemoveCreator'));
      return;
    }
    try {
      await apiService.removeTournamentOrganizer(selectedTournament.id, userId);
      setSuccess('Organizer removed');
      loadOrganizers(selectedTournament.id);
      loadTournaments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove organizer');
    }
  };

  const handleApproveMatch = async (match: any) => {
    try {
      // Use match_id if available (for TournamentMatch), otherwise use id (for regular Match)
      const regularMatchId = match.match_id || match.id;
      await apiService.approveTournamentMatch(regularMatchId);
      setSuccess('Match approved successfully!');
      if (selectedTournament) {
        loadMatches(selectedTournament.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve match');
    }
  };

  const handleRejectMatch = async (match: any, reason?: string) => {
    try {
      // Use match_id if available (for TournamentMatch), otherwise use id (for regular Match)
      const regularMatchId = match.match_id || match.id;
      await apiService.rejectTournamentMatch(regularMatchId, reason);
      setSuccess('Match rejected successfully!');
      setRejectDialogOpen(false);
      setSelectedMatchForReject(null);
      setRejectReason('');
      if (selectedTournament) {
        loadMatches(selectedTournament.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject match');
    }
  };

  const openRejectDialog = (match: Match) => {
    setSelectedMatchForReject(match);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleLaunchMontanteRound = async () => {
    if (!selectedTournament || launchingMontanteRound) return;
    try {
      setLaunchingMontanteRound(true);
      const response = await apiService.launchMontanteRound(selectedTournament.id);
      setSuccess(`Round ${response.data.round_number} launched successfully (${response.data.matches_created} matches created)`);
      loadMatches(selectedTournament.id);
      const canLaunchResponse = await apiService.canLaunchMontanteRound(selectedTournament.id);
      setCanLaunchMontanteRound(canLaunchResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to launch round');
    } finally {
      setLaunchingMontanteRound(false);
    }
  };

  const handleLaunchMexicanoRound = async () => {
    if (!selectedTournament || launchingMexicanoRound) return;
    try {
      setLaunchingMexicanoRound(true);
      const response = await apiService.launchMexicanoRound(selectedTournament.id);
      const sittingOut = response.data.players_sitting_out || 0;
      setSuccess(
        `Round ${response.data.round_number} launched (${response.data.matches_created} matches)` +
          (sittingOut ? ` — ${sittingOut} player(s) sitting out` : '')
      );
      loadMatches(selectedTournament.id);
      const canLaunchResponse = await apiService.canLaunchMexicanoRound(selectedTournament.id);
      setCanLaunchMexicanoRound(canLaunchResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to launch round');
    } finally {
      setLaunchingMexicanoRound(false);
    }
  };

  const handleLaunchAmericanoRound = async () => {
    if (!selectedTournament || launchingAmericanoRound) return;
    try {
      setLaunchingAmericanoRound(true);
      const response = await apiService.launchAmericanoRound(selectedTournament.id);
      const sittingOut = response.data.players_sitting_out || 0;
      setSuccess(
        `Round ${response.data.round_number} launched (${response.data.matches_created} matches)` +
          (sittingOut ? ` — ${sittingOut} player(s) sitting out` : '')
      );
      loadMatches(selectedTournament.id);
      const canLaunchResponse = await apiService.canLaunchAmericanoRound(selectedTournament.id);
      setCanLaunchAmericanoRound(canLaunchResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to launch round');
    } finally {
      setLaunchingAmericanoRound(false);
    }
  };

  const handleApproveParticipant = async (participantId: number) => {
    if (!selectedTournament) return;
    
    try {
      await apiService.approveTournamentParticipant(selectedTournament.id, participantId);
      setSuccess('Participant approved successfully!');
      loadParticipants(selectedTournament.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve participant');
    }
  };

  const handleRejectParticipant = async (participantId: number) => {
    if (!selectedTournament) return;
    
    try {
      await apiService.rejectTournamentParticipant(selectedTournament.id, participantId);
      setSuccess('Participant rejected successfully!');
      loadParticipants(selectedTournament.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject participant');
    }
  };

  const handleRemoveParticipant = (participant: TournamentParticipant) => {
    if (!selectedTournament) return;

    setConfirmTitle(t('adminTournament.confirmRemoveTitle'));
    setConfirmMessage(
      t('adminTournament.confirmRemoveMessage', {
        label: participantDisplayLabel(participant),
        tournament: selectedTournament.name,
      })
    );
    setConfirmAction(() => async () => {
      try {
        await apiService.removeTournamentParticipant(selectedTournament.id, participant.id);
        setSuccess('Participant removed successfully!');
        loadParticipants(selectedTournament.id);
        setOpenConfirmDialog(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to remove participant');
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleCreateTournament = async () => {
    setEditingTournament(null);
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      registration_deadline: '',
      max_participants: 16,
      min_skill_level: 1,
      max_skill_level: 10,
      format: 'round_robin',
      status: 'upcoming',
      format_config: null,
      club_ids: [],
    });
    try {
      const clubsRes = await apiService.getClubs();
      setClubs(clubsRes.data || []);
    } catch {
      setClubs([]);
    }
    setCourts([]);
    setOpenDialog(true);
  };

  const handleEditTournament = async (tournament: Tournament) => {
    setEditingTournament(tournament);
    const fc = (tournament as any).format_config;
    const clubIds = (tournament as any).club_ids || [];
    setFormData({
      name: tournament.name,
      description: tournament.description || '',
      start_date: tournament.start_date,
      end_date: tournament.end_date,
      registration_deadline: '',
      max_participants: tournament.max_participants || 16,
      min_skill_level: 1,
      max_skill_level: 10,
      format: (tournament.format || 'round_robin') as Tournament['format'],
      status: (tournament.status as 'upcoming' | 'registration' | 'active' | 'completed' | 'cancelled') || 'upcoming',
      format_config: fc && typeof fc === 'object' ? fc : null,
      club_ids: clubIds,
    });
    try {
      const clubsRes = await apiService.getClubs();
      setClubs(clubsRes.data || []);
      if (clubIds.length > 0) {
        const courtsRes = await apiService.getCourts(clubIds);
        setCourts(courtsRes.data || []);
      } else {
        setCourts([]);
      }
    } catch {
      setClubs([]);
      setCourts([]);
    }
    setOpenDialog(true);
  };

  const handleSubmitTournament = async () => {
    try {
      const payload: any = { ...formData };
      const formatConfig: Record<string, number | number[]> = {};
      if (formData.format === 'montante_descendante' && formData.format_config?.num_groups) {
        formatConfig.num_groups = formData.format_config.num_groups;
        formatConfig.max_teams_per_court = formData.format_config.max_teams_per_court ?? 2;
        const courtIds = formData.format_config.court_ids?.filter((id: number) => id > 0) || [];
        if (courtIds.length === formData.format_config.num_groups) {
          formatConfig.court_ids = courtIds;
        }
      }
      if (
        (formData.format === 'swiss_system' || formData.format === 'mexicano' || formData.format === 'americano')
        && formData.format_config?.num_rounds
      ) {
        formatConfig.num_rounds = formData.format_config.num_rounds;
      }
      if (formData.format === 'round_robin' && formData.format_config?.num_groups) {
        formatConfig.num_groups = formData.format_config.num_groups;
      }
      payload.format_config = Object.keys(formatConfig).length > 0 ? formatConfig : undefined;
      payload.club_ids = formData.club_ids ?? [];

      if (editingTournament) {
        await apiService.updateTournament(editingTournament.id, payload);
      } else {
        await apiService.createTournament(payload);
      }

      setOpenDialog(false);
      setEditingTournament(null);
      loadTournaments();
      setSuccess('Tournament saved successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save tournament');
    }
  };

  const handleDeleteTournament = () => {
    if (!selectedTournament) return;

    setConfirmTitle(t('adminTournament.confirmDeleteTitle'));
    setConfirmMessage(
      t('adminTournament.confirmDeleteMessage', { name: selectedTournament.name })
    );
    setConfirmAction(() => async () => {
      try {
        await apiService.deleteTournament(selectedTournament.id);
        setSelectedTournament(null);
        loadTournaments();
        setSuccess('Tournament deleted successfully!');
        setOpenConfirmDialog(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to delete tournament');
      }
    });
    setOpenConfirmDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'upcoming':
        return 'info';
      case 'registration':
        return 'success';
      case 'active':
        return 'warning';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getParticipantStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const pendingParticipants = participants.filter(p => p.status === 'pending');
  const approvedParticipants = participants.filter(p => p.status === 'approved');
  const rejectedParticipants = participants.filter(p => p.status === 'rejected');
  const waitingMatches = matches.filter(m => {
    const status = (m.status || '').toLowerCase();
    return status === 'waiting_admin_confirmation' || status === 'waitingadminconfirmation';
  });
  const confirmedMatches = matches.filter(m => {
    const status = (m.status || '').toLowerCase();
    return status === 'confirmed';
  });
  const rejectedMatches = matches.filter(m => {
    const status = (m.status || '').toLowerCase();
    return status === 'rejected';
  });

  if (loading) {
    return <LoadingSpinner message="Loading tournament management..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Tournament Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage tournaments, participants, and matches
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateTournament}
        >
          Create Tournament
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Tournaments List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tournaments ({tournaments.length})
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {tournaments.map((tournament) => (
                  <Card
                    key={tournament.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: selectedTournament?.id === tournament.id ? 'primary.light' : 'background.paper',
                      '&:hover': { backgroundColor: 'primary.light' },
                    }}
                    onClick={() => setSelectedTournament(tournament)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {tournament.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Chip
                          label={tournament.status}
                          color={getStatusColor(tournament.status) as any}
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {tournament.current_participants || 0}/{tournament.max_participants}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tournament Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          {selectedTournament ? (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h5">
                    {selectedTournament.name}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <IconButton onClick={() => handleEditTournament(selectedTournament)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={handleDeleteTournament}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body1" color="text.secondary" paragraph>
                  {selectedTournament.description || 'No description available'}
                </Typography>

                <Box display="flex" gap={2} mb={3}>
                  <Chip
                    label={selectedTournament.status}
                    color={getStatusColor(selectedTournament.status) as any}
                  />
                  <Chip
                    label={selectedTournament.format || 'round_robin'}
                    variant="outlined"
                  />
                  <Chip
                    label={`${selectedTournament.current_participants || 0}/${selectedTournament.max_participants} participants`}
                    icon={<People />}
                    variant="outlined"
                  />
                </Box>

                {/* Share link section */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Share sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {`${window.location.origin}/t/${selectedTournament.id}`}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Share />}
                    onClick={() => {
                      const url = `${window.location.origin}/t/${selectedTournament.id}`;
                      navigator.clipboard.writeText(url).then(() => {
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      });
                    }}
                  >
                    {copySuccess ? (t('tournamentPublic.linkCopied') || 'Link copied!') : (t('tournamentPublic.copyLink') || 'Copy link')}
                  </Button>
                </Box>

                {/* Organizers section */}
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('adminTournament.organizers')}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                    {loadingOrganizers ? (
                      <Typography variant="body2" color="text.secondary">{t('adminTournament.loading')}</Typography>
                    ) : (
                      <>
                        {organizers.map((org) => {
                          const uid = org.user_id ?? (org as any).userId;
                          const isCreator = uid === (selectedTournament as any).created_by;
                          const name =
                            org.user_name ??
                            (org as any).userName ??
                            usersList.find((u) => u.id === uid)?.name ??
                            (selectedTournament as any)?.organizer_names?.[
                              (selectedTournament as any)?.organizer_ids?.indexOf(uid) ?? -1
                            ];
                          const displayName = (name && String(name).trim()) || `User ${uid ?? '?'}`;
                          return (
                            <Chip
                              key={uid}
                              label={displayName}
                              size="small"
                              variant="outlined"
                              onDelete={isCreator ? undefined : () => handleRemoveOrganizer(uid)}
                              sx={{ mb: 0.5 }}
                            />
                          );
                        })}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAdd />}
                          onClick={() => setAddOrganizerDialogOpen(true)}
                        >
                          {t('adminTournament.addOrganizer')}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Management Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label={`Pending (${pendingParticipants.length})`} />
                    <Tab label={`Approved (${approvedParticipants.length})`} />
                    <Tab label={`Rejected (${rejectedParticipants.length})`} />
                    <Tab label={`Matches (${waitingMatches.length})`} icon={<SportsTennis />} iconPosition="start" />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Player</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Applied</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingParticipants.map((participant) => (
                          <TableRow key={participant.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {participant.player1_name?.charAt(0) || '?'}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {participant.player1_name || 'Unknown Player'}
                                  </Typography>
                                  {participant.player2_name && (
                                    <Typography variant="body2" color="text.secondary">
                                      Partner: {participant.player2_name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={participant.status}
                                color={getParticipantStatusColor(participant.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {participant.applied_at ? new Date(participant.applied_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={1}>
                                <Tooltip title="Approve">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApproveParticipant(participant.id)}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRejectParticipant(participant.id)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveParticipant(participant)}
                                  >
                                    <PersonRemove />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {pendingParticipants.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary">
                                No pending participants
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Player</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Matches</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {approvedParticipants.map((participant) => (
                          <TableRow key={participant.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {participant.player1_name?.charAt(0) || '?'}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {participant.player1_name || 'Unknown Player'}
                                  </Typography>
                                  {participant.player2_name && (
                                    <Typography variant="body2" color="text.secondary">
                                      Partner: {participant.player2_name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={participant.status}
                                color={getParticipantStatusColor(participant.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {participant.matches_played || 0} played, {participant.matches_won || 0} won
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveParticipant(participant)}
                                >
                                  <PersonRemove />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {approvedParticipants.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary">
                                No approved participants
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Player</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Applied</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rejectedParticipants.map((participant) => (
                          <TableRow key={participant.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {participant.player1_name?.charAt(0) || '?'}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {participant.player1_name || 'Unknown Player'}
                                  </Typography>
                                  {participant.player2_name && (
                                    <Typography variant="body2" color="text.secondary">
                                      Partner: {participant.player2_name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={participant.status}
                                color={getParticipantStatusColor(participant.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {participant.applied_at ? new Date(participant.applied_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveParticipant(participant)}
                                >
                                  <PersonRemove />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {rejectedParticipants.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary">
                                No rejected participants
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                {/* Matches Tab */}
                <TabPanel value={tabValue} index={3}>
                  {(() => {
                    const isMontante = String(selectedTournament?.format || '').toLowerCase() === 'montante_descendante' ||
                      String(selectedTournament?.format || '').toLowerCase() === 'montantedescendante';
                    const isMontanteOrganizerOrAdmin = user?.is_admin || user?.id === (selectedTournament as any)?.created_by;
                    const showMontanteLaunch = isMontante && selectedTournament?.status === 'active' && isMontanteOrganizerOrAdmin;

                    const isMexicano = String(selectedTournament?.format || '').toLowerCase() === 'mexicano';
                    const showMexicanoLaunch = isMexicano && selectedTournament?.status === 'active' && isMontanteOrganizerOrAdmin;
                    const isAmericano = String(selectedTournament?.format || '').toLowerCase() === 'americano';
                    const showAmericanoLaunch = isAmericano && selectedTournament?.status === 'active' && isMontanteOrganizerOrAdmin;

                    if (showMontanteLaunch) {
                      return (
                      <Box mb={3}>
                        <Card variant="outlined" sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              Round management
                            </Typography>
                            {canLaunchMontanteRound?.can_launch ? (
                              <Button
                                variant="contained"
                                startIcon={launchingMontanteRound ? undefined : <PlayArrow />}
                                onClick={handleLaunchMontanteRound}
                                disabled={launchingMontanteRound}
                              >
                                {launchingMontanteRound ? 'Launching round...' : `Launch Round ${canLaunchMontanteRound.next_round}`}
                              </Button>
                            ) : canLaunchMontanteRound?.reason ? (
                              <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                                <Info fontSize="small" />
                                <Typography variant="body2">{canLaunchMontanteRound.reason}</Typography>
                              </Box>
                            ) : null}
                          </CardContent>
                        </Card>
                      </Box>
                      );
                    }

                    if (showMexicanoLaunch) {
                      return (
                      <Box mb={3}>
                        <Card variant="outlined" sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              Round management
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              Players are paired by current standings (1 &amp; 4 vs 2 &amp; 3). Complete every match before launching the next round.
                            </Typography>
                            {canLaunchMexicanoRound?.can_launch ? (
                              <Button
                                variant="contained"
                                startIcon={launchingMexicanoRound ? undefined : <PlayArrow />}
                                onClick={handleLaunchMexicanoRound}
                                disabled={launchingMexicanoRound}
                              >
                                {launchingMexicanoRound ? 'Launching round...' : `Launch Round ${canLaunchMexicanoRound.next_round}`}
                              </Button>
                            ) : canLaunchMexicanoRound?.reason ? (
                              <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                                <Info fontSize="small" />
                                <Typography variant="body2">{canLaunchMexicanoRound.reason}</Typography>
                              </Box>
                            ) : null}
                          </CardContent>
                        </Card>
                      </Box>
                      );
                    }

                    if (showAmericanoLaunch) {
                      return (
                      <Box mb={3}>
                        <Card variant="outlined" sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              Round management
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              Partners rotate each round on every court. Complete every match before launching the next round.
                            </Typography>
                            {canLaunchAmericanoRound?.can_launch ? (
                              <Button
                                variant="contained"
                                startIcon={launchingAmericanoRound ? undefined : <PlayArrow />}
                                onClick={handleLaunchAmericanoRound}
                                disabled={launchingAmericanoRound}
                              >
                                {launchingAmericanoRound ? 'Launching round...' : `Launch Round ${canLaunchAmericanoRound.next_round}`}
                              </Button>
                            ) : canLaunchAmericanoRound?.reason ? (
                              <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                                <Info fontSize="small" />
                                <Typography variant="body2">{canLaunchAmericanoRound.reason}</Typography>
                              </Box>
                            ) : null}
                          </CardContent>
                        </Card>
                      </Box>
                      );
                    }

                    return null;
                  })()}
                  {loadingMatches ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <LoadingSpinner message="Loading matches..." />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell>Court</TableCell>
                            <TableCell>Players</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {waitingMatches.map((match: any) => {
                            // Handle both TournamentMatch (with team1_name/team2_name) and regular Match (with participants_data)
                            let team1Names = 'TBD';
                            let team2Names = 'TBD';
                            
                            if (match.team1_name && match.team2_name) {
                              // TournamentMatch format
                              team1Names = match.team1_name;
                              team2Names = match.team2_name;
                            } else if (match.participants_data) {
                              // Regular Match format
                              const team1Players = match.participants_data.filter((p: any) => p.team_number === 1) || [];
                              const team2Players = match.participants_data.filter((p: any) => p.team_number === 2) || [];
                              team1Names = team1Players.map((p: any) => p.user_name || p.guest_player_name || 'Unknown').join(' & ') || 'TBD';
                              team2Names = team2Players.map((p: any) => p.user_name || p.guest_player_name || 'Unknown').join(' & ') || 'TBD';
                            }
                            
                            const matchDate = match.match_date || match.date;
                            const matchTime = match.time;
                            
                            return (
                              <TableRow key={match.id || match.match_id}>
                                <TableCell>
                                  <Typography variant="body2">
                                    {matchDate ? new Date(matchDate).toLocaleDateString() : 'TBD'}
                                  </Typography>
                                  {matchTime && (
                                    <Typography variant="body2" color="text.secondary">
                                      {matchTime}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {match.court_name || 'TBD'}
                                  </Typography>
                                  {match.club_name && (
                                    <Typography variant="body2" color="text.secondary">
                                      {match.club_name}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    <strong>Team 1:</strong> {team1Names}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Team 2:</strong> {team2Names}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={match.status}
                                    color={match.status === 'waiting_admin_confirmation' ? 'warning' : match.status === 'confirmed' ? 'success' : 'error'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const status = (match.status || '').toLowerCase();
                                    const isWaiting = status === 'waiting_admin_confirmation' || status === 'waitingadminconfirmation';
                                    return isWaiting ? (
                                      <Box display="flex" gap={1}>
                                        <Tooltip title="Approve Match">
                                          <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => handleApproveMatch(match)}
                                          >
                                            <CheckCircle />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reject Match">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => openRejectDialog(match)}
                                          >
                                            <Cancel />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ) : null;
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {waitingMatches.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                <Typography color="text.secondary">
                                  No matches waiting for approval
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </TabPanel>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                  <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a Tournament
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a tournament from the list to manage participants and matches
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Add Organizer Dialog */}
      <Dialog
        open={addOrganizerDialogOpen}
        onClose={() => {
          setAddOrganizerDialogOpen(false);
          setSelectedUserToAdd(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('adminTournament.addOrganizerTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('adminTournament.addOrganizerDescription')}
          </Typography>
          <Autocomplete
            options={usersList.filter(
              (u) => !organizers.some((o) => o.user_id === u.id)
            )}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={selectedUserToAdd}
            onChange={(_, newValue) => setSelectedUserToAdd(newValue)}
            renderInput={(params) => (
              <TextField {...params} label={t('adminTournament.selectUser')} placeholder={t('adminTournament.searchUserPlaceholder')} />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddOrganizerDialogOpen(false);
              setSelectedUserToAdd(null);
            }}
          >
            {t('adminTournament.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddOrganizer}
            disabled={!selectedUserToAdd}
          >
            {t('adminTournament.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Match Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => {
        setRejectDialogOpen(false);
        setSelectedMatchForReject(null);
        setRejectReason('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Match</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejecting this match..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setSelectedMatchForReject(null);
            setRejectReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (selectedMatchForReject) {
                handleRejectMatch(selectedMatchForReject, rejectReason);
              }
            }}
          >
            Reject Match
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Tournament Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTournament ? 'Edit Tournament' : 'Create Tournament'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Tournament Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Registration Deadline"
                type="date"
                value={formData.registration_deadline}
                onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Max Participants"
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={formData.format}
                  label="Format"
                  onChange={(e) => {
                    const fmt = e.target.value as Tournament['format'];
                    setFormData({
                      ...formData,
                      format: fmt,
                      format_config:
                        fmt === 'montante_descendante'
                          ? { num_groups: formData.format_config?.num_groups ?? 4 }
                          : fmt === 'swiss_system'
                            ? { num_rounds: formData.format_config?.num_rounds }
                            : fmt === 'round_robin'
                              ? { num_groups: formData.format_config?.num_groups }
                              : fmt === 'mexicano' || fmt === 'americano'
                                ? { num_rounds: formData.format_config?.num_rounds }
                                : null,
                    });
                  }}
                >
                  <MenuItem value="round_robin">Round Robin</MenuItem>
                  <MenuItem value="single_elimination">Single Elimination</MenuItem>
                  <MenuItem value="double_elimination">Double Elimination</MenuItem>
                  <MenuItem value="swiss_system">Swiss System</MenuItem>
                  <MenuItem value="league">League</MenuItem>
                  <MenuItem value="flex_league">Flex League</MenuItem>
                  <MenuItem value="montante_descendante">Montante/Descendante</MenuItem>
                  <MenuItem value="americano">Americano</MenuItem>
                  <MenuItem value="mexicano">Mexicano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.format === 'montante_descendante' && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Number of groups (courts)"
                    type="number"
                    inputProps={{ min: 2, max: 8 }}
                    value={formData.format_config?.num_groups ?? 4}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        format_config: {
                          ...(formData.format_config || {}),
                          num_groups: parseInt(e.target.value, 10) || 4,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Max teams per court</InputLabel>
                    <Select
                      value={formData.format_config?.max_teams_per_court ?? 2}
                      label="Max teams per court"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          format_config: {
                            ...(formData.format_config || {}),
                            max_teams_per_court: Number(e.target.value) || 2,
                          },
                        })
                      }
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.club_ids && formData.club_ids.length > 0 && courts.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Select courts (in order: Court 1, Court 2, ...)</Typography>
                    {Array.from({ length: formData.format_config?.num_groups || 4 }, (_, i) => (
                      <Box key={i} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 70 }}>Court {i + 1}:</Typography>
                        <Select
                          size="small"
                          value={formData.format_config?.court_ids?.[i] ?? ''}
                          displayEmpty
                          onChange={(e) => {
                            const val = Number(e.target.value) || 0;
                            const current = formData.format_config?.court_ids || [];
                            const next = [...current];
                            while (next.length <= i) next.push(0);
                            next[i] = val;
                            setFormData({
                              ...formData,
                              format_config: {
                                ...(formData.format_config || {}),
                                court_ids: next,
                              },
                            });
                          }}
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value={0}>Select court</MenuItem>
                          {courts.map((court: any) => (
                            <MenuItem key={court.id} value={court.id}>
                              {court.name} {court.club_name ? `(${court.club_name})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    ))}
                  </Grid>
                )}
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Participating clubs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Clubs where players can create tournament matches. Select at least one for match creation.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {clubs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No clubs available
                  </Typography>
                ) : (
                  clubs.map((club: any) => {
                    const isSelected = formData.club_ids?.includes(club.id);
                    return (
                      <Chip
                        key={club.id}
                        label={club.name}
                        onClick={() => {
                          const nextIds = isSelected
                            ? (formData.club_ids || []).filter((id: number) => id !== club.id)
                            : [...(formData.club_ids || []), club.id];
                          setFormData({ ...formData, club_ids: nextIds });
                        }}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                      />
                    );
                  })
                )}
              </Box>
            </Grid>
            {(formData.format === 'swiss_system' || formData.format === 'mexicano' || formData.format === 'americano') && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Number of rounds (optional)"
                  type="number"
                  value={formData.format_config?.num_rounds ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      format_config: {
                        ...(formData.format_config || {}),
                        num_rounds: parseInt(e.target.value, 10) || undefined,
                      },
                    })
                  }
                />
              </Grid>
            )}
            {formData.format === 'round_robin' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Number of groups (optional)"
                  type="number"
                  value={formData.format_config?.num_groups ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      format_config: {
                        ...(formData.format_config || {}),
                        num_groups: parseInt(e.target.value, 10) || undefined,
                      },
                    })
                  }
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="registration">Registration Open</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitTournament} variant="contained">
            {editingTournament ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={t('adminTournament.confirm')}
        cancelLabel={t('adminTournament.cancel')}
        severity="error"
      />
    </Box>
  );
};
